// Request schemas (zod). The API validates every input with these; clients use them for forms and types.
import { z } from 'zod';
import {
  ACTIVITY_STATUSES, ACTIVITY_TYPES, ACTION_KINDS, CONVERSATION_PURPOSES, ACTION_STATUSES, CHECK_AUDIENCES, CONTEXT_MAX_CHARS, CONTEXT_PROPOSAL_MAX_CHARS, CONTEXT_SECTIONS,
  DEVICE_PLATFORMS, FOLLOW_FREQUENCIES, INSIGHT_CATEGORIES, INSIGHT_CONFIDENCE, KB_MAX_BYTES, NOTIFICATION_CATEGORIES, NOTIFICATION_KINDS, RESEARCH_LEVELS, ROLES, SESSION_CLIENTS, SPACE_COLORS, SPEECH_LANGUAGES, sectionsFor,
} from './enums.js';

// Validate without generated code, everywhere. Zod probes Function('') when it builds an object schema, to see
// whether it may compile faster validators; in the browser, under the production Content-Security-Policy
// (script-src 'self', no 'unsafe-eval'), that probe is a CSP violation and a console error on every page load.
// It must run before the first schema below is built, so it sits here and not in the web app's entry.
z.config({ jitless: true });

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optText = (max: number) => z.string().trim().max(max).nullish();
export const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');
export const IsoDateTime = z.iso.datetime({ offset: true });
export const IsoDate = z.iso.date();
const boolString = z.enum(['true', 'false']).transform(v => v === 'true');

export const IdParams = z.object({ id: z.uuid() });
/** POST /seen: you looked at it. kb and research take no id. space: "Mark all as seen" for that space. */
export const SEEN_KINDS = ['space', 'track', 'activity', 'updates', 'minutes', 'files', 'kb', 'research'] as const;
export type SeenKind = typeof SEEN_KINDS[number];
export const SeenBody = z.object({ kind: z.enum(SEEN_KINDS), id: z.uuid().nullish() })
  .refine(b => (b.kind === 'kb' || b.kind === 'research') === !b.id, { message: 'Give the id of what you looked at (none for kb and research).' });
/** Offset pagination: lists return { items, nextCursor } and never truncate silently. */
export const PageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(100),
  cursor: z.string().regex(/^\d{1,7}$/).optional(),
});
export const Version = z.number().int().positive();
export const SpaceUserParams = z.object({ id: z.uuid(), userId: z.uuid() });

// ---------- auth and users ----------
export const LoginBody = z.object({
  email: z.string().trim().min(3).max(200),
  password: z.string().min(1).max(500),
  /** "app" returns a bearer token in the body instead of setting a cookie (native app). */
  client: z.enum(SESSION_CLIENTS).default('web'),
});
export const PASSWORD_MIN = 10;
export const SetPasswordBody = z.object({ token: z.string().min(20).max(200), password: z.string().min(10).max(500) });
export const PasswordChangeBody = z.object({ current: z.string().min(1).max(500), next: z.string().min(PASSWORD_MIN).max(500) });
export const UserCreateBody = z.object({
  email: z.email().max(200), name: trimmed(120), role: z.enum(ROLES).default('member'), isAdmin: z.boolean().default(false),
  /** Optional (5 Nov): left out, the person is emailed a link to set their own password. */
  password: z.union([z.string().min(PASSWORD_MIN).max(500), z.literal('').transform(() => undefined)]).optional(), title: optText(120),
});
export const UserPatchBody = z.object({
  name: trimmed(120).optional(), role: z.enum(ROLES).optional(), isAdmin: z.boolean().optional(), isActive: z.boolean().optional(), title: optText(120),
});

// ---------- spaces, contacts, tracks ----------
export const SpaceBody = z.object({ name: trimmed(120), clientName: optText(120), description: optText(2000), color: z.enum(SPACE_COLORS).optional() });
/** A new space: its lead too (the person creating it when not given). Changing the lead later is Members. */
export const SpaceCreate = SpaceBody.extend({ leadUserId: z.uuid().nullish() });
export const SpacePatch = SpaceBody.partial();
export const MemberBody = z.object({ userId: z.uuid(), role: z.enum(['lead', 'member']).default('member') });
export const ContactBody = z.object({ name: trimmed(120), title: optText(120), organisation: optText(120), email: z.email().max(200).nullish() });
export const ContactPatch = ContactBody.partial();
export const TrackBody = z.object({
  name: trimmed(120), description: optText(2000), leadUserId: z.uuid().nullish(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
});
export const TrackPatch = TrackBody.partial();

// ---------- activities ----------
export const PersonRef = z.object({ kind: z.enum(['user', 'contact']), id: z.uuid() });
export type PersonRef = z.infer<typeof PersonRef>;

const activityFields = {
  type: z.enum(ACTIVITY_TYPES),
  title: trimmed(200),
  description: optText(20000),
  status: z.enum(ACTIVITY_STATUSES).optional(),
  /** One or more owners, primary first. Defaults to the person creating it. */
  ownerIds: z.array(z.uuid()).min(1, 'An activity needs at least one owner').max(10).refine(a => new Set(a).size === a.length, 'Owners are listed twice').optional(),
  location: optText(200),
  startsAt: IsoDateTime.nullish(),
  endsAt: IsoDateTime.nullish(),
  dueDate: IsoDate.nullish(),
  dueTime: HHMM.nullish(),
  isMilestone: z.boolean().optional(),
  needsPrep: z.boolean().optional(),
  progress: z.number().int().min(0).max(100).optional(),
  requestedBy: PersonRef.nullish(),
  waitingOn: PersonRef.nullish(),
  tags: z.array(z.string().trim().min(1).max(40).regex(/^[^\s,]+$/, 'Tags have no spaces or commas')).max(20).optional(),
  parentActivityId: z.uuid().nullish(),
  /** A follow-up: the meeting (or activity) it came from ("From"), set when it is made from minutes or an email. */
  originActivityId: z.uuid().nullish(),
  attendees: z.array(PersonRef).max(60).optional(),
};
export const StepInput = z.object({ title: trimmed(300), ownerId: z.uuid().nullish() });
export const ActivityCreate = z.object({ trackId: z.uuid(), ...activityFields, steps: z.array(StepInput).max(60).optional() });
export type ActivityCreate = z.infer<typeof ActivityCreate>;
export const ActivityPatch = z.object({ trackId: z.uuid(), ...activityFields }).partial();
export type ActivityPatch = z.infer<typeof ActivityPatch>;
/** A PATCH must say which version it was made against (body "version" or an If-Match header). */
export const ActivityPatchBody = ActivityPatch.extend({ version: Version.optional() });
export type ActivityPatchBody = z.infer<typeof ActivityPatchBody>;
export const QuickAddBody = z.object({ title: trimmed(200) });
/** Natural-language quick add: the words typed so far, and the track the row is on (the default). */
export const QuickParseBody = z.object({ text: trimmed(500), trackId: z.uuid().nullish() });
export type QuickParseBody = z.infer<typeof QuickParseBody>;
export const StepPatch = z.object({ title: trimmed(300).optional(), ownerId: z.uuid().nullish(), done: z.boolean().optional() });
export const StepsReorderBody = z.object({ stepIds: z.array(z.uuid()).min(1).max(60) });
export const UpdateBody = z.object({ body: trimmed(20000) });
export const ActivityListQuery = PageQuery.extend({
  trackId: z.uuid().optional(),
  /** Words in the title. */
  q: z.string().trim().min(1).max(100).optional(),
  type: z.enum(ACTIVITY_TYPES).optional(),
  mine: boolString.optional(),
  includeDone: boolString.optional(),
  clientRequests: boolString.optional(),
});

// ---------- reminders ----------
const reminderWhen = (x: { remindAt?: string | null; remindDate?: string | null }) => !!x.remindAt !== !!x.remindDate;
const WHEN_MSG = 'Give a date and time, or a date only (one of them).';
export const ReminderCreate = z.object({
  text: trimmed(500), remindAt: IsoDateTime.nullish(), remindDate: IsoDate.nullish(), activityId: z.uuid().nullish(),
  /** Who the reminder is for. Defaults to the person setting it. */
  forUserId: z.uuid().nullish(),
}).refine(reminderWhen, WHEN_MSG);
export type ReminderCreate = z.infer<typeof ReminderCreate>;
export const ReminderPatch = z.object({
  text: trimmed(500).optional(), remindAt: IsoDateTime.nullish(), remindDate: IsoDate.nullish(), activityId: z.uuid().nullish(),
  version: Version.optional(),
});
export type ReminderPatch = z.infer<typeof ReminderPatch>;

// ---------- calendar ----------
export const CalendarQuery = z.object({ from: IsoDate, to: IsoDate, spaceId: z.uuid().optional(), mine: boolString.optional() });
export type CalendarQueryInput = z.input<typeof CalendarQuery>;

// ---------- the approval engine ----------
/** An insight added by hand (Research Center), or by approving the assistant's add_insight card. */
export const InsightAdd = z.object({
  /** A link, or pasted text (one of them). */
  url: z.url({ protocol: /^https?$/ }).max(500).nullish(), text: z.string().trim().max(20_000).nullish(),
  why: optText(500), category: z.enum(INSIGHT_CATEGORIES).nullish(),
}).refine(x => !!x.url !== !!x.text, 'Give a link or paste the text (one of them).');
export const ActionPayloads = {
  create_activity: ActivityCreate,
  create_reminder: ReminderCreate,
  /** Put an attached file on an activity, a track, or in a Knowledge base folder (exactly one). */
  add_file: z.object({ fileId: z.uuid(), activityId: z.uuid().nullish(), trackId: z.uuid().nullish(), folderId: z.uuid().nullish() })
    .refine(x => [x.activityId, x.trackId, x.folderId].filter(Boolean).length === 1, 'Say which activity, track or folder the file goes in (one).'),
  update_activity: z.object({
    activityId: z.uuid(), changes: ActivityPatch,
    /** Step changes, in order: add (before a step, or at the end), remove, mark done or not done. */
    steps: z.array(z.discriminatedUnion('op', [
      z.object({ op: z.literal('add'), title: trimmed(300), beforeStepId: z.uuid().nullish(), ownerId: z.uuid().nullish() }),
      z.object({ op: z.literal('remove'), stepId: z.uuid() }),
      z.object({ op: z.literal('done'), stepId: z.uuid(), done: z.boolean().default(true) }),
    ])).max(20).optional(),
  }),
  send_message: z.object({ to: trimmed(200), subject: optText(200), body: trimmed(10000), activityId: z.uuid().nullish() }),
  file_minutes: z.object({
    activityId: z.uuid(), minutes: trimmed(50000),
    actions: z.array(z.object({
      title: trimmed(200), ownerIds: z.array(z.uuid()).min(1).max(10).optional(), dueDate: IsoDate.nullish(), type: z.enum(['task', 'deliverable']).optional(),
      /** A recording: the voice ("Speaker 2") who owns it. Its confirmed name decides the owner; unconfirmed, whoever approves. */
      speaker: z.string().max(40).optional(),
    })).max(30).optional(),
  }),
  /** Team answers disagree: the asker picks one (chosenAnswerId) or says the right answer (combinedText). */
  confirm_answer: z.object({
    checkId: z.uuid(),
    answers: z.array(z.object({ answerId: z.uuid(), userId: z.uuid(), name: trimmed(120), answer: trimmed(4000), answeredAt: IsoDateTime })).min(1).max(50),
    chosenAnswerId: z.uuid().nullish(),
    combinedText: optText(4000),
  }),
  /**
   * Add to (append) or rewrite (replace) one section of a Space or Track context. baseVersion is set by the
   * server to the doc version the card was made against; approving checks it again.
   */
  update_context: z.object({
    target: z.enum(['space', 'track']), targetId: z.uuid(), section: z.enum(CONTEXT_SECTIONS),
    operation: z.enum(['append', 'replace']), text: trimmed(CONTEXT_PROPOSAL_MAX_CHARS),
    baseVersion: z.number().int().positive().optional(),
  }).refine(x => sectionsFor(x.target).includes(x.section), 'That section does not exist on this kind of context.'),
  /** Nobody answered in time: ask the people who have not answered again, and give them more time. */
  nudge_check: z.object({ checkId: z.uuid(), extendHours: z.number().int().min(1).max(168).default(24) }),
  /** Ask the owners of an activity (or one person) for an update: what Today's Nudge sends, as a team check. */
  check_in: z.object({
    question: trimmed(500), activityId: z.uuid(),
    /** owners: the activity's owners. person: one named person (personId), e.g. the colleague it waits on. */
    audience: z.enum(['owners', 'person']).default('owners'), personId: z.uuid().nullish(),
    expiresInHours: z.number().int().min(1).max(168).default(48),
  }),
  /** A partner's question has answers: the director approves one, writes the answer, or changes it by saying what to change. */
  review_answer: z.object({
    checkId: z.uuid(),
    conflict: z.boolean(),
    answers: z.array(z.object({ answerId: z.uuid(), userId: z.uuid(), name: trimmed(120), answer: trimmed(4000), answeredAt: IsoDateTime })).min(1).max(50),
    chosenAnswerId: z.uuid().nullish(),
    combinedText: optText(4000),
    /** People asked who have not answered yet; review can start before everyone answers. */
    stillWaiting: z.number().int().min(0).default(0),
  }),
  /**
   * An email to the inbound address the assistant could not place (never proposed by the assistant in a chat). reason:
   * why, in plain words. Approving is refused until a person picks the space and track (retarget), which makes it a
   * new activity or an update like any forwarded email.
   */
  log_email: z.object({ reason: trimmed(300) }),
  /** A research task at a level (the assistant picks it from the question and says which; "go deeper" upgrades it). */
  start_research: z.object({ question: trimmed(500), level: z.enum(RESEARCH_LEVELS) }),
  /** Watch a link: what to look out for, how often. Something new arrives as insights to keep or dismiss. */
  follow_link: z.object({ url: z.url({ protocol: /^https?$/ }).max(500), name: optText(120), watchFor: optText(300), frequency: z.enum(FOLLOW_FREQUENCIES).default('daily') }),
  /** A topic for the scheduled research runs (for the Research lead or an admin to accept; the assistant never adds one). */
  add_research_topic: z.object({ text: trimmed(200), why: optText(300) }),
  /** Add an insight to the Research Center from a link or pasted text (approving reads it, as Add an insight by hand does). */
  add_insight: InsightAdd,
  /** A new space (admins create spaces, as in Settings). */
  create_space: SpaceBody,
  /** A new track in a space. */
  create_track: TrackBody.extend({ spaceId: z.uuid() }),
  /** Add someone to a space, or change their role there (lead or member). */
  set_member: z.object({ spaceId: z.uuid(), userId: z.uuid(), role: z.enum(['lead', 'member']).default('member') }),
  /** Take someone off a space (what they owned there goes to the space lead). */
  remove_member: z.object({ spaceId: z.uuid(), userId: z.uuid() }),
  /** Archive a space or a track: it leaves every list until restored from Settings, Archived. Nothing is deleted. */
  archive: z.object({ target: z.enum(['space', 'track']), id: z.uuid() }),
} as const;
export type ActionPayloadMap = { [K in keyof typeof ActionPayloads]: z.infer<(typeof ActionPayloads)[K]> };
export const ACTION_KIND_SET = new Set<string>(ACTION_KINDS);

/** for=everyone: every person's cards (the director only). */
export const ActionListQuery = PageQuery.extend({ status: z.enum(['waiting', 'all']).default('waiting'), for: z.enum(['me', 'everyone']).default('me') });
export const ActionChangeBody = z.object({ text: trimmed(2000) });
/** A forwarded email card: where it goes (track) and what it becomes (an update on an activity there, or a new one). */
export const ActionRetargetBody = z.object({ trackId: z.uuid(), activityId: z.uuid().nullable() });
export const ActionApproveBody = z.object({ version: z.number().int().positive().optional() });
export const ActionStatusEnum = z.enum(ACTION_STATUSES);

// ---------- assistant ----------
/**
 * A new conversation. The panel opens one scoped to where you are: a space, a track or an activity (the space follows
 * from the track or activity), and what it was opened for (purpose).
 */
export const ConversationCreate = z.object({
  title: optText(120), spaceId: z.uuid().nullish(), trackId: z.uuid().nullish(), activityId: z.uuid().nullish(),
  /** A Knowledge base folder or file: the assistant answers from its text. */
  folderId: z.uuid().nullish(), fileId: z.uuid().nullish(),
  purpose: z.enum(CONVERSATION_PURPOSES).nullish(),
});
/** Change what the conversation looks at: a space or Everything (null). A track or activity focus is cleared. */
export const ConversationPatch = z.object({ spaceId: z.uuid().nullable() });
/** spoken: a turn said out loud in Talk (transcribed speech; the reply is read out). */
/**
 * screen: voice control sends what is on the screen (the page's path, its title and a short structured summary), so
 * "what am I looking at" and "open the second one" can be answered. Never stored.
 */
export const ScreenSummary = z.object({ path: z.string().max(300), title: z.string().max(200), summary: z.string().max(6000) });
export type ScreenSummary = z.infer<typeof ScreenSummary>;
export const MessageBody = z.object({ content: trimmed(8000), spoken: z.boolean().optional(), screen: ScreenSummary.optional() });
export const DescribeBody = z.object({ text: trimmed(8000), spaceId: z.uuid(), trackId: z.uuid().nullish() });

// ---------- team checks ----------
export const CheckCreate = z.object({
  question: trimmed(2000),
  activityId: z.uuid().nullish(), trackId: z.uuid().nullish(), spaceId: z.uuid().nullish(),
  /** person: ask one named person (personId). owners: the owners of what it is about. everyone: the whole team. */
  audience: z.enum(CHECK_AUDIENCES).exclude(['director']).default('owners'),
  personId: z.uuid().nullish(),
  /** The conversation the answer goes back to (partners). */
  conversationId: z.uuid().nullish(),
  expiresInHours: z.number().int().min(1).max(168).default(24),
}).refine(x => [x.activityId, x.trackId, x.spaceId].filter(Boolean).length <= 1, 'A check is about one activity, track or space at most.')
  .refine(x => (x.audience === 'person') === !!x.personId, 'Name the person (personId) when the audience is one person, and only then.');
export type CheckCreate = z.input<typeof CheckCreate>;
export const CheckAnswerBody = z.object({ answer: trimmed(4000) });
export const CheckConfirmBody = z.object({ answerId: z.uuid().nullish(), text: optText(4000) })
  .refine(x => !!x.answerId !== !!x.text, 'Pick one answer, or write the right answer (one of them).');
export const CheckDivertBody = z.object({ userIds: z.array(z.uuid()).min(1).max(10) });
export const CheckSendBackBody = z.object({ question: trimmed(2000), userIds: z.array(z.uuid()).min(1).max(10).optional() });
export const CheckListQuery = PageQuery.extend({ scope: z.enum(['asked', 'to_answer', 'all']).default('all') });

// ---------- devices (push) ----------
export const DeviceBody = z.object({ platform: z.enum(DEVICE_PLATFORMS), token: trimmed(4096) });
/** Dismiss all: every open row, or one kind's (the filter on screen). */
export const DismissAllBody = z.object({ kind: z.enum(NOTIFICATION_KINDS).optional() }).default({});
/** Undo a Dismiss all: the rows it dismissed. */
export const RestoreManyBody = z.object({ ids: z.array(z.uuid()).max(2000) });

// ---------- Space and Track context ----------
export const ContextSectionKey = z.enum(CONTEXT_SECTIONS);
/** Save one section by hand (an empty text clears it). version: the doc version you edited. */
export const ContextPatchBody = z.object({
  section: ContextSectionKey,
  text: z.string().max(CONTEXT_MAX_CHARS).transform(t => t.replace(/\s+$/, '')),
  version: Version.optional(),
});

// ---------- reminders from words (typed or spoken) ----------
const ChannelsPatch = z.object({ push: z.boolean().optional(), email: z.boolean().optional() });
/** Change some settings; anything left out stays as it is. */
export const UserPrefsPatch = z.object({
  notifications: z.object({
    channels: z.partialRecord(z.enum(NOTIFICATION_CATEGORIES), ChannelsPatch).optional(),
    quietHours: z.object({ from: HHMM, to: HHMM }).nullable().optional(),
  }).optional(),
  openGroups: z.array(z.uuid()).max(100).optional(),
  voice: z.object({ language: z.enum(SPEECH_LANGUAGES) }).optional(),
  morningEmail: z.boolean().optional(),
});
/** The forwarded email a card came from, as Requests shows it (the log@ inbox fills this). */
export const EmailOrigin = z.object({
  from: z.string().optional(), to: z.string().optional(), receivedAt: z.string().optional(), subject: z.string().optional(),
  body: z.string().optional(), attachments: z.array(z.object({ name: z.string(), size: z.number().int().nonnegative().optional(), fileId: z.string().optional() })).optional(),
  /** Forwarded to log@: the forwarder's own words above the forward, and who forwarded it. */
  note: z.string().nullish(), forwardedBy: z.string().optional(),
  /**
   * The parts of the card that can be left out before approving: a forwarded email's change, follow-up or
   * attachment; a recording's actions and meetings to set up.
   */
  extras: z.array(z.object({ id: z.string(), label: z.string(), include: z.boolean(), kind: z.enum(['change', 'follow_up', 'attach', 'action', 'setup']) })).optional(),
});
export type EmailOrigin = z.infer<typeof EmailOrigin>;

export const ReminderParseBody = z.object({ text: trimmed(1000) });

// ---------- Research Center ----------
export const ResearchTaskCreate = z.object({ question: trimmed(500), level: z.enum(RESEARCH_LEVELS).default('standard') });
export const ResearchTaskAnswer = z.object({ answer: trimmed(1000) });
export const InsightPatch = z.object({
  title: trimmed(200).optional(), category: z.enum(INSIGHT_CATEGORIES).optional(), summary: trimmed(1000).optional(), why: z.string().trim().max(500).optional(),
  trackIds: z.array(z.uuid()).max(10).optional(),
  /** Tags without the #: lower case, no spaces (a space becomes a dash), at most 8. */
  tags: z.array(z.string().trim().toLowerCase().transform(t => t.replace(/^#+/, '').replace(/\s+/g, '-')).pipe(z.string().min(1).max(40).regex(/^[^,#]+$/, 'Tags have no commas'))).max(8).optional(),
  /** Confidence set by hand; null goes back to working it out from the data. */
  confidence: z.enum(INSIGHT_CONFIDENCE).nullable().optional(),
});
/** "Contradicts...": this insight and another disagree. note: what disagrees (optional). */
export const ContradictionCreate = z.object({ otherId: z.uuid(), note: optText(300) });
/** Resolve a contested pair: the one that holds up (keepId; the other is dismissed), or keep both with a note. */
export const ContradictionResolve = z.union([
  z.object({ keepId: z.uuid(), note: optText(500) }),
  z.object({ keepBoth: z.literal(true), note: trimmed(500) }),
]);
/** Keep (verify) or dismiss. anyway: keep although a quote was not found in its source (asked once). */
export const InsightDecide = z.object({ decision: z.enum(['keep', 'dismiss', 'undo']), anyway: z.boolean().optional() });
export const InsightListQuery = z.object({
  category: z.enum(INSIGHT_CATEGORIES).optional(), status: z.enum(['new', 'kept', 'all']).default('all'), source: z.string().max(200).optional(),
  q: z.string().trim().max(200).optional(),
});
export const FollowCreate = z.object({ url: z.url({ protocol: /^https?$/ }).max(500), name: optText(120), watchFor: optText(300), frequency: z.enum(FOLLOW_FREQUENCIES).default('daily') });
export const FollowPatch = z.object({ watchFor: optText(300), frequency: z.enum(FOLLOW_FREQUENCIES).optional(), name: trimmed(120).optional() });
export const TopicCreate = z.object({ text: trimmed(200) });
export const ResearchLeadBody = z.object({ userId: z.uuid().nullable() });
export const InsightSaveBody = z.object({ folderId: z.uuid() });

// ---------- Knowledge base (document folders) ----------
export const FolderCreate = z.object({
  name: trimmed(120), description: z.string().trim().max(2000).default(''),
  /** null or absent: Team-wide. */
  spaceId: z.uuid().nullish(),
});
export type FolderCreate = z.input<typeof FolderCreate>;
/** Rename, describe, or move (spaceId; null moves it to Team-wide). */
export const FolderPatch = z.object({
  name: trimmed(120).optional(), description: z.string().trim().max(2000).optional(),
  spaceId: z.uuid().nullable().optional(),
});
export type FolderPatch = z.input<typeof FolderPatch>;
export const FolderListQuery = z.object({ spaceId: z.uuid().optional(), teamWide: boolString.optional() });
const fileName = z.string().trim().min(1).max(200).refine(n => !/[\\/\r\n"]/.test(n), 'A file name cannot contain slashes, quotes or line breaks.');
/** Start a presigned upload. On a same-name file in the folder, resend with resolve: version (add as version N) or keep_both. */
export const UploadCreate = z.object({
  name: fileName, size: z.number().int().min(1).max(KB_MAX_BYTES, 'Files can be up to 50 MB.'), mime: z.string().trim().max(200).default(''),
  resolve: z.enum(['version', 'keep_both']).optional(),
});
export type UploadCreate = z.input<typeof UploadCreate>;
/** "Upload new version" on a file. */
export const VersionUploadCreate = UploadCreate.omit({ resolve: true });
export const VersionParams = z.object({ id: z.uuid(), version: z.coerce.number().int().positive() });
export const LinkFolderBody = z.object({ folderId: z.uuid() });
export const FileFolderParams = z.object({ id: z.uuid(), folderId: z.uuid() });
const summaryText = (max: number) => z.string().trim().max(max);
export const FileSummaryBody = z.object({
  whatItIs: summaryText(1000),
  keyPoints: z.array(z.object({ text: trimmed(600), ref: z.number().int().positive().nullable() })).max(8),
  dates: z.array(z.object({ date: trimmed(60), what: trimmed(300) })).max(12),
  peopleOrgs: z.array(z.object({ name: trimmed(120), role: summaryText(200) })).max(16),
  numbers: z.array(z.object({ value: trimmed(60), what: trimmed(300) })).max(12),
  length: summaryText(60), language: summaryText(60),
});
/** Edit a summary by hand (also "describe it by hand" for an unreadable file). */
export const SummaryPatch = z.object({ summary: FileSummaryBody });
/** Rewrite with the assistant. A person edited it: confirm: true is needed, or the answer is 409 confirm_rewrite. */
export const RewriteBody = z.object({ confirm: z.boolean().default(false) });
/** Save generated text (an insight, a report) into a folder as a file, with its summary (Research Center). */
export const SaveToFolderBody = z.object({
  title: trimmed(160), format: z.enum(['text', 'pdf']).default('text'), text: z.string().trim().min(1).max(400_000),
  summary: FileSummaryBody.optional(),
});
export type SaveToFolderBody = z.input<typeof SaveToFolderBody>;
export const SearchQuery = z.object({ q: z.string().trim().min(2).max(200), spaceId: z.uuid().optional(), limit: z.coerce.number().int().min(1).max(50).default(20) });
/** The Search page. tags: comma separated, with or without #. */
export const SearchAllQuery = z.object({
  q: z.string().trim().min(2).max(200),
  kind: z.enum(['activity', 'track', 'minutes', 'email', 'file', 'person', 'insight']).optional(),
  tags: z.string().max(400).optional().transform(t => (t ? t.split(',').map(x => x.trim()).filter(Boolean).slice(0, 8) : [])),
});
/** A forwarded email's card: leave out one part (a change, a follow-up, an attachment), or put it back. */
export const ActionExtraBody = z.object({ id: z.string().min(1).max(20), include: z.boolean() });
// ---------- meetings ----------
export const RecordingStart = z.object({
  activityId: z.uuid().nullish(),
  /** A new meeting from this recording: its track and title. */
  trackId: z.uuid().nullish(), title: z.string().trim().max(200).nullish(),
}).refine(b => !!b.activityId || !!b.trackId, 'Choose the meeting, or a track for a new one.');
export const RecordingStop = z.object({ durationSecs: z.number().min(0).max(4 * 60 * 60), marks: z.array(z.number().min(0)).max(100).optional() });
export const RecordingPartParams = z.object({ id: z.uuid(), seq: z.coerce.number().int().min(0).max(2000) });
/** Words to read out (Listen, Talk). */
export const UsageQuery = z.object({ range: z.enum(['week', '4weeks', 'quarter']).default('week'), userId: z.uuid().optional() });
export const SpeakBody = z.object({ text: z.string().trim().min(1).max(5000) });
export const SpeakerName = z.object({ label: z.string().trim().min(1).max(40), name: z.string().trim().min(1).max(120) });
export const MeetingsQuery = z.object({ when: z.enum(['all', 'upcoming', 'past']).optional(), spaceId: z.uuid().optional() });
export const MeetingAskBody = z.object({ question: z.string().trim().min(2).max(500) });
export const MeetingLogBody = z.object({
  activityId: z.uuid().nullish(),
  trackId: z.uuid().nullish(), title: z.string().trim().max(200).nullish(), startsAt: IsoDateTime.nullish(), endsAt: IsoDateTime.nullish(),
  minutes: trimmed(50000),
  actions: z.array(z.object({ title: z.string().trim().max(200), ownerIds: z.array(z.uuid()).max(10).optional(), dueDate: IsoDate.nullish() })).max(30).optional(),
});
export const SavedSearchBody = z.object({
  name: z.string().trim().max(80).optional(),
  q: z.string().trim().min(2).max(200),
  kind: z.enum(['activity', 'track', 'minutes', 'email', 'file', 'person', 'insight']).nullable().optional(),
  tags: z.array(z.string().trim().min(1).max(60)).max(8).optional(),
});
export const SavedSearchRename = z.object({ name: z.string().trim().min(1).max(80) });
export const SearchAskBody = z.object({ q: z.string().trim().min(2).max(500) });
export const FolderAskBody = z.object({ question: z.string().trim().min(2).max(500) });
